import { Box, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import { theme } from "styles/theme";
import { RightArrowSvg } from "../../static/image/common/SvgAssets";
import { SupplementaryTaskType } from "../../api";
import useOpenTask from "../../hooks/useOpenTask";
import React from "react";
import CustomTypography from "../common/CustomTypography";
import { supplementaryTaskListAtom } from "../../recoil/supplementaryTaskListAtom";

interface TaskSectionProps {
  taskList: SupplementaryTaskType[];
}

const TaskSection: React.FC<TaskSectionProps> = ({ taskList }) => {
  // 각 항목을 별도 컴포넌트로 분리하여 hook을 최상위에서 사용
  const TaskRow: React.FC<{ task: SupplementaryTaskType }> = ({ task }) => {
    const openTask = useOpenTask({ taskKey: task.taskKey });
    return (
      <Stack
        direction={"row"}
        width={"100%"}
        height={"48px"}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        onClick={openTask}
      >
        <CustomTypography
          variant={"Body"}
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
          }}
        >
          {task.taskTitle}
        </CustomTypography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 0,
            color: theme.vars.palette.Content.Weak,
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
          }}
        >
          <RightArrowSvg color={theme.vars.palette.Content.SuperWeak} />
        </Box>
      </Stack>
    );
  };

  return taskList.length > 0 ? (
    <Stack
      justifyContent="space-between"
      sx={{
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        py: "8px",
        px: "20px",
      }}
    >
      {taskList.map((task, index) => (
        <TaskRow key={`${task.taskKey}-${index}`} task={task} />
      ))}
    </Stack>
  ) : (
    <></>
  );
};

export default function MyConcernAndGoal({
  concern,
  goal,
}: {
  concern?: string;
  goal?: string;
}) {
  const taskListState = useRecoilValue(taskListAtom);
  const supplementaryList = useRecoilValue(supplementaryTaskListAtom);
  const retrospectList = supplementaryList.filter((e) =>
    e.taskId.includes("0-3-W")
  );
  const rangeCheckList = supplementaryList.filter((e) => e.taskId === "0-8-A");

  const hasSeqOne = retrospectList.some((e) => e.seq === 1);

  const is1_2_A_done = taskListState.find(
    (element) => element.taskId === "1-2-A"
  )?.lastDoneAt;

  const data = [
    {
      label: "고민",
      text: is1_2_A_done ? concern : null,
      placeholder: "요즈음 나를 괴롭히는 고민은 무엇인가요?",
    },
    {
      label: "목표",
      text: is1_2_A_done ? goal : null,
      placeholder: "내가 만들고 싶은 변화는 무엇인가요?",
    },
  ];

  const someEmpty = data.some(
    ({ text }) => !text || String(text).trim().length === 0
  );
  if (someEmpty) {
    return (
      <Stack
        direction={"column"}
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", minHeight: "70vh" }}
      >
        <Typography
          variant={"Body"}
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {"아직 목표를 세우지 않았어요.\n" +
            "코치의 안내에 따라 활동을 진행해주세요."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack
        spacing={2}
        sx={{
          p: "20px",
          backgroundColor: "white",
          borderRadius: "12px",
        }}
      >
        {data.map(({ label, text, placeholder }, index) => (
          <Box key={index}>
            <Stack spacing={0.5}>
              <Typography
                variant={"Caption"}
                sx={{
                  px: 0,
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,
                }}
                color="primary"
              >
                {label}
              </Typography>
              <Typography
                variant={"Body"}
                sx={{
                  px: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 500,
                }}
              >
                {text || placeholder}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>

      <TaskSection taskList={retrospectList}></TaskSection>
      {retrospectList.length > 0 && !hasSeqOne && (
        <Stack sx={{ px: "8px", pt: "4px", pb: "24px" }}>
          <Typography
            variant={"Caption"}
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontWeight: 500,
            }}
          >
            {"2025년 8월 말 이후의 <이번 주 돌아보기>부터 보여집니다."}
          </Typography>
        </Stack>
      )}
      <TaskSection taskList={rangeCheckList}></TaskSection>
    </Stack>
  );
}
